import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { fetchAdvancedMetrics } from "../../../lib/loggerApi";
import type { AdvancedMetrics } from "../types";

/**
 * PMA-COCK-098.AC7 contention fix (2026-09-22, EPIC-68, xg-fe T4): the
 * catch-up refetch below is a bounded RETRY SEQUENCE, not a single shot.
 * `CATCHUP_MAX_ATTEMPTS` total fetch attempts, with `CATCHUP_BACKOFF_MS[i]`
 * ms between attempt `i+1` and `i+2` (exponential, 1s/2s/4s/8s). Worst case
 * ~15s of wall time after the falling edge before giving up for good — long
 * enough to ride out the backend's metrics pass finishing under real load
 * (the exact contention `noise-qa`'s remeasurement caught: a one-shot
 * catch-up can itself land before `app/services/metrics_scheduler.py`
 * commits), short enough that a genuinely-never-arriving value does not
 * poll forever.
 */
const CATCHUP_MAX_ATTEMPTS = 5;
const CATCHUP_BACKOFF_MS = [1000, 2000, 4000, 8000];

/**
 * `isLive` (PMA-COCK-098 follow-up, 2026-08-06): xG is a slow-moving
 * aggregate, but with no polling and no WS-driven invalidation anywhere in
 * the codebase, the cockpit's `stat-xg` row never updated during a live
 * match — it painted the correct pending em dash at mount and then simply
 * never refetched, even as an analyst logged real Shot events and every
 * other stat row moved. The public page doesn't have this gap; it holds
 * `advancedMetrics` in its own component state, refreshed by its
 * page-level 10s poll (`PublicMatchAnalytics.tsx`), which is unaffected by
 * this hook either way.
 *
 * Fix is a live-gated poll, not a WS-driven invalidation: `refetchInterval`
 * only while the caller says the match is live, `false` (disabled)
 * otherwise — so nothing polls once a match ends. No tighter than the
 * existing `staleTime`, since xG doesn't need sub-second freshness and a
 * WS-side invalidation would touch the cockpit's most delicate code for a
 * stats row that can tolerate a 30s lag. React Query's default
 * `refetchIntervalInBackground: false` already pauses this while the tab
 * is hidden/unfocused, at no extra cost. Does not touch this hook's
 * guarded error-isolation invariant (E2) — no `throwOnError`, unchanged;
 * a failing poll still never breaks the analytics view, it just leaves the
 * row on `data-xg-state="pending"` until the next successful tick.
 *
 * PMA-COCK-218 (noise-qa lane, EPIC-68, live-repro'd via
 * scripted-match.spec.ts's worst-case-match): the live-gated poll above
 * has a real gap at the edge it was built to stop at. If the match's
 * live -> finished transition lands INSIDE a 30s poll gap, the last poll
 * before the transition can still show `generated_at: null` (the
 * backend's own metrics pass, `app/services/metrics_scheduler.py`, hasn't
 * finished yet), and the moment `isLive` flips to false this hook stops
 * polling for good — nothing else ever refetches or invalidates the query
 * key again. The cockpit is then stuck showing the pending em dash
 * forever against a real, already-computed server value, for the rest of
 * the session. Fix (original, AC7): exactly ONE catch-up refetch, fired on
 * the live -> non-live FALLING EDGE only.
 *
 * PMA-COCK-098.AC7 amendment (2026-09-22, same epic, `noise-qa`'s AC4
 * remeasurement under 4-worker load): the one-shot above narrows the race
 * but does not close it — `scripted-match.spec.ts:382` failed again with
 * the byte-identical signature (`Expected: "0.00"` / `Received: "—"`),
 * because the single catch-up fetch can itself land before the backend's
 * metrics pass has finished computing; under contention-induced latency
 * that window widens rather than shrinks. Fix: the falling edge now starts
 * a bounded, backoff RETRY SEQUENCE (`CATCHUP_MAX_ATTEMPTS` attempts,
 * `CATCHUP_BACKOFF_MS` between them) instead of a single fetch — it keeps
 * retrying only while the cached snapshot is still `generated_at: null`,
 * stops the instant a real value arrives (no extra fetch beyond the one
 * that got it), and gives up for good once the attempt budget is spent, so
 * a match that genuinely never gets a metrics value does not poll forever.
 * Never a second thing schedules once the sequence ends — no resumed
 * periodic polling, no new timer outside this one bounded chain — this
 * still preserves the "nothing polls once a match ends" contract the tests
 * below pin, it just widens "the falling edge itself is silent" from one
 * attempt to a bounded handful.
 */
export function useAdvancedMetrics(
  matchId: string | undefined,
  isLive: boolean = false,
) {
  const queryClient = useQueryClient();
  const wasLiveRef = useRef(isLive);

  useEffect(() => {
    const isFallingEdge = wasLiveRef.current && !isLive && Boolean(matchId);
    wasLiveRef.current = isLive;
    if (!isFallingEdge || !matchId) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const queryKey = ["advancedMetrics", matchId];

    const attempt = (attemptNumber: number) => {
      queryClient.refetchQueries({ queryKey }).then(() => {
        if (cancelled) return;
        const data = queryClient.getQueryData<AdvancedMetrics>(queryKey);
        if (data?.generated_at != null) return; // converged — stop for good
        if (attemptNumber >= CATCHUP_MAX_ATTEMPTS) return; // bounded — give up
        const delay = CATCHUP_BACKOFF_MS[attemptNumber - 1];
        timeoutId = setTimeout(() => attempt(attemptNumber + 1), delay);
      });
    };

    attempt(1);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [isLive, matchId, queryClient]);

  return useQuery<AdvancedMetrics, Error>({
    queryKey: ["advancedMetrics", matchId],
    queryFn: () => fetchAdvancedMetrics(matchId!),
    enabled: Boolean(matchId),
    staleTime: 30_000,
    retry: 1,
    refetchInterval: isLive ? 30_000 : false,
  });
}
